import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Button } from '@wf-common/atoms'
import styles from './tooltip-button.module.css'

const propTypes = {
    ...Button.propTypes,
    wrapperClassName: PropTypes.string
}

/**
 * A Button Component which can be used inside tooltips. Regular buttons don't trigger mouseleave and -enter events,
 * when they're disabled so this component adds a wrapper around the button and disables pointer events completely
 * when the button is disabled.
 *
 * @example
 * <code>
 *     <Tooltip>
 *          <TooltipButton {...buttonProps} />
 *      </Tooltip>
 * </code>
 *
 *
 * Edge case opening a Dialog/Modal: If the TooltipButton is used to open a Dialog/Modal the TooltipButton gets back
 * the focus after closing the Dialog/Modal. This causes the Tooltip to be shown again.
 * To omit this behaviour prevent the event propagation on mouse down events. See the example below.
 *
 * @example
 * <code>
 *      <Tooltip>
 *          <TooltipButton {...buttonProps} onClick={openTargetDialog} onMouseDown={(e) => e.preventDefault()}/>
 *      </Tooltip>
 * </code>
 *
 * @param {Function} onMouseEnter
 * @param {Function} onMouseLeave
 * @param {Function} onFocus
 * @param {Function} onMouseDown
 * @param {Function} onTouchStart
 * @param {Function} onBlur
 * @param {boolean} disabled
 * @param {string} className - the className prop which is forwarded to the button
 * @param {string} wrapperClassName - the className which can be set on the wrapping div
 * @param {object} props - all props which don't get handled by this component and get forwarded to <Button />
 * @return {React.Component}
 */
function TooltipButton({
    className,
    disabled,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onTouchStart,
    wrapperClassName,
    ...props
}) {
    const composedWrapperClassName = cx('t3sel-tooltip-button-wrapper', styles['wrapper'], wrapperClassName)

    const composedButtonClassName = cx('t3sel-tooltip-button-button', styles['button'], className)

    const wrapperProps = {
        onBlur,
        onFocus,
        onMouseDown,
        onMouseEnter,
        onMouseLeave,
        onTouchStart
    }

    return (
        <div {...{ ...wrapperProps, className: composedWrapperClassName }}>
            <Button {...{ ...props, className: composedButtonClassName, disabled }} />
        </div>
    )
}

TooltipButton.propTypes = propTypes

export { TooltipButton }
